import React from "react";
import { Button, Typography } from "../../components/common";
import { Download, ArrowLeft, Share2, Linkedin } from "lucide-react";
import { useCertificateDetailHook } from "./useCertificateDetailHook";
import PedagogueLogo from "../../images/PedagogueLogo";
import { formatDate } from "../../utils/utils";
import PedagogueCertificateText from "../../images/PedagogueCertificateText";
import { LinkedInShareModal } from "../../components/Certificate/LinkedInShareModal";

const generatingCopy =
  "Certificate is generating, please check back in a few minutes";
const failedCopy =
  "Your certificate is taking longer than expected — we're retrying automatically. If it doesn't appear shortly, contact support.";

/**
 * ⚠ RETIRED 2026-08-29 — the constant below returns false and nothing here
 * watermarks. This docblock is retained HISTORY: it records why the predicate was
 * a conjunction, which is precisely what a reinstatement would need.
 *
 * Mirror of `shouldRenderTrialWatermark` in
 * backend/pedagogue-learn-lms-api/src/certificates/certificate-render-state.ts,
 * the source of truth for every render path (the PDF Handlebars templates, the
 * regeneration operator, and this page). Duplicated rather than imported —
 * the app bundle does not reach into the API workspace — so change both together.
 *
 * Neither flag can decide this alone. `isTrial` is provenance and stays true
 * forever on a certificate earned during a trial, so watermarking on it alone
 * stamps SAMPLE across a credential the customer has since upgraded and paid
 * for. `isPedagogueCertified` alone is worse: the backend also sends it false
 * for a course that simply is not Pedagogue-certified and for a plan without
 * the certificates feature, so a paying customer's ordinary completion
 * certificate would come back marked "NOT FOR DISTRIBUTION".
 *
 * The certified flag is optional on the wire. A response that predates it
 * leaves it undefined, which collapses this to the previous `isTrial`-only
 * behaviour, so neither a stale bundle against a new backend nor a new bundle
 * against an old one regresses.
 */
// RETIRED 2026-08-29 (CTO product ruling): the trial SAMPLE watermark is removed
// entirely. Mirrors `shouldRenderTrialWatermark` in the backend's
// certificate-render-state.ts, which now also returns false. Kept as a single
// switch rather than deleted, so the two sides stay in lockstep if it is ever
// reinstated. NOTE: this does NOT close the E2 race — the Passport stamp and
// Learning Hours still gate on `is_pedagogue_certified_at_issue`.
// The learner-side mirror of the backend's `shouldRenderTrialWatermark` USED to live here.
// It is deleted, not commented out: the overlay it gated was removed, so the function had no
// caller and CI builds with warnings-as-errors. Reinstating the trial watermark therefore means
// RE-ADDING this mirror plus its overlay markup, alongside the three backend sites in R121.14.

const CertificateDetail: React.FC = () => {
  const {
    certificate,
    certificateRef,
    handleDownload,
    handleShare,
    handleBackToCertificates,
    isLoading,
    error,
    showLinkedInModal,
    setShowLinkedInModal,
    pendingLinkedInUrl,
  } = useCertificateDetailHook();

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-4 shadow-lg">
              <Download className="w-8 h-8 text-white" />
            </div>
            <Typography
              variant="h1"
              weight="bold"
              className="mb-2 text-textPrimary"
            >
              Loading Certificate
            </Typography>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
            <Typography variant="p" className="mt-4 text-textPrimary-grey">
              Please wait while we load your certificate...
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-16">
            <Typography
              variant="h2"
              weight="bold"
              className="mb-3 text-textPrimary"
            >
              Unable to Load Certificate ☹️
            </Typography>
            <Typography
              variant="p"
              className="text-textPrimary-grey max-w-md mx-auto mb-4 leading-relaxed"
            >
              We encountered an issue while fetching your certificate. This
              might be due to a temporary network issue or server problem.
            </Typography>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="filled"
                onClick={handleBackToCertificates}
                startIcon={<ArrowLeft className="w-4 h-4" />}
              >
                Back to Certificates
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If no certificate, don't render anything (redirect will happen)
  if (!certificate) {
    return null;
  }

  // Bypass historical previews for non-certified courses because old generated
  // artefacts may carry certification branding that was not earned at issue.
  const hasPreviewImage =
    certificate.thumbnail_uri && certificate.isPedagogueCertified === true;
  const downloadDisabled = !certificate.download_uri;
  const pdfStatusCopy = downloadDisabled
    ? certificate.pdf_status === "failed"
      ? failedCopy
      : generatingCopy
    : null;

  return (
    <div className="min-h-screen p-2 sm:p-4">
      <LinkedInShareModal
        showLinkedInModal={showLinkedInModal}
        setShowLinkedInModal={setShowLinkedInModal}
        pendingLinkedInUrl={pendingLinkedInUrl}
      />
      <div className=" mx-auto">
        {/* Header */}

        {/* Certificate Display */}
        <div
          className="bg-white max-w-4xl shadow-sm mx-auto overflow-hidden relative"
          ref={certificateRef}
        >
          {/* Sample watermark — REMOVED. The overlay markup is gone and its gating
              const with it (it was unreferenced and failed the CI warnings-as-errors
              build). Reinstating means restoring both, plus the other three sites in
              R121.14. See
              shouldRenderTrialWatermark above for why it was a conjunction and
              not `isTrial` alone. Also gated on
              !hasPreviewImage: the server-rendered `thumbnail_uri` PNG has the
              same watermark baked in by the Handlebars template, so an overlay
              on top of it would double-stamp. Mobile uses an at-the-top banner
              (Tailwind `md:hidden`) for readability; desktop/tablet
              uses a 30°-rotated semi-transparent overlay matching the
              PDF template style at backend/.../certificates/templates/
              {course,level}-certificate.hbs (.trial-watermark CSS). */}
          {/* If we have a server-generated preview image, show that instead of the fallback layout */}
          {hasPreviewImage ? (
            <div className="w-full bg-gray-50">
              <img
                src={certificate.thumbnail_uri || ""}
                alt="Certificate preview"
                className="w-full h-auto block"
                crossOrigin="anonymous"
              />
            </div>
          ) : (
            <>
              {/* Certificate Header - Dark Blue/Grey Background */}
              <div className="relative bg-pd-ink text-white p-4">
                {/* Header Content */}
                <div className="relative z-10">
                  <div className="flex justify-between items-start mb-6">
                    {/* Logo */}
                    <PedagogueLogo className="w-full h-full  " />

                    {/* ID */}
                    <Typography variant="small" className="!text-slate-400">
                      ID: {certificate?.id}
                    </Typography>
                  </div>

                  {/* Pedagogue text spanning bottom */}
                  <div className="absolute -bottom-12 left-0 right-0">
                    <PedagogueCertificateText className="w-full h-full" />
                  </div>

                  {/* Certified Name */}
                  <div className="text-center mb-8">
                    <Typography
                      variant="caption"
                      className="!text-slate-400 tracking-wider mb-2"
                    >
                      {certificate.isPedagogueCertified
                        ? "CERTIFIED:"
                        : "COMPLETED BY:"}
                    </Typography>
                    <Typography
                      variant="h1"
                      weight="medium"
                      className="text-white"
                    >
                      {certificate?.user_name ? (
                        <>
                          <span>{certificate?.user_name?.split(" ")[0]}</span>
                          {certificate?.user_name?.split(" ").length > 1 && (
                            <span className="text-pd-green ml-2">
                              {certificate?.user_name
                                ?.split(" ")
                                .slice(1)
                                .join(" ")}
                            </span>
                          )}
                        </>
                      ) : (
                        <span>-</span>
                      )}
                    </Typography>
                  </div>
                </div>
              </div>

              {/* Certificate Body - White Background */}
              <div className="px-4 py-10 bg-white">
                {certificate?.isPedagogueCertified && (
                  <div className="flex justify-center mb-6">
                    <div className="rounded-full border border-blue-200 flex items-center justify-center bg-blue-50 px-4 py-2">
                      <Typography
                        variant="small"
                        weight="bold"
                        className="text-blue-700"
                      >
                        Pedagogue Certified
                      </Typography>
                    </div>
                  </div>
                )}

                {/* Course Title */}
                <Typography
                  variant="h2"
                  weight="bold"
                  className="text-gray-800 text-center mb-1"
                >
                  {certificate?.course_name}
                </Typography>

                {/* Course Description */}
                <Typography
                  variant="p"
                  className="text-gray-600 text-center mb-10 md:max-w-[48%] mx-auto leading-relaxed"
                >
                  {certificate?.course_description}
                </Typography>

                {/* Progress Bar */}
                <div className="md:max-w-[48%] mx-auto">
                  <div className="h-[5px] bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-green-400 via-purple-400 via-orange-400 via-pink-400 to-blue-400 rounded-full"></div>
                  </div>
                  <Typography
                    variant="p"
                    weight="medium"
                    className="text-center text-gray-700 mt-10"
                  >
                    Course completed: {certificate.course_name}
                  </Typography>
                </div>
              </div>

              {/* Certificate Footer - White Background */}
              <div className="p-1 sm:p-2 md:p-4 bg-gray-50 border-t border-gray-100">
                <div className="flex justify-between items-end">
                  {/* Date of Completion */}
                  <div className="flex flex-col ">
                    <Typography
                      variant="caption"
                      weight="medium"
                      className="!text-slate-400 mb-4"
                    >
                      DATE OF COMPLETION:
                    </Typography>
                    <Typography
                      variant="small"
                      weight="medium"
                      className="text-gray-800"
                    >
                      {formatDate(certificate?.issued_at)}
                    </Typography>
                  </div>

                  {/* Issuer Logo and Name (hide when no org branding) */}
                  {certificate.organisation_name &&
                  certificate.organisation_name !== "Organisation" ? (
                      <div className="text-center">
                        {/* TODO: Replace with real org logo when available */}
                        <Typography
                          variant="small"
                          weight="medium"
                          className="text-gray-800"
                        >
                          {certificate.organisation_name}
                        </Typography>
                      </div>
                    ) : (
                      <div />
                    )}

                  <div />
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex flex-wrap flex-row items-center justify-between mt-6 gap-4 ">
          <Button
            variant="outline"
            onClick={handleBackToCertificates}
            startIcon={<ArrowLeft className="w-4 h-4" />}
            className="text-gray-600 hover:text-gray-800 w-full sm:w-auto text-sm sm:text-base px-3 sm:px-4 py-2 sm:py-2.5"
          >
            <span className="hidden text-sm sm:inline">
              Back to Certificates
            </span>
            <span className="text-xs sm:hidden">Back</span>
          </Button>

          <div className="flex flex-col  sm:flex-row gap-3 sm:gap-2 w-full sm:w-auto">
            {/* Native Share Button */}
            <Button
              variant="outline"
              onClick={handleShare}
              startIcon={<Share2 className="w-4 h-4" />}
              className="text-gray-600 hover:text-gray-800 text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5"
            >
              Share
            </Button>

            {/* Social Media Share Buttons */}
            <span className="relative group">
              <Button
                variant="filled"
                disabled
                className="!bg-gray-300 !text-gray-500 !cursor-not-allowed px-5 py-1.5 text-sm font-medium rounded-full flex items-center gap-1.5"
              >
                <Linkedin className="w-3.5 h-3.5" />
                LinkedIn
              </Button>
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                Digital Badge sharing coming soon
              </span>
            </span>

            <Button
              variant="filled"
              onClick={handleDownload}
              disabled={downloadDisabled}
              aria-label="Download certificate"
              data-testid="cert-download-btn"
              title={pdfStatusCopy || "Download Certificate"}
              className={
                downloadDisabled ? "opacity-50 cursor-not-allowed" : ""
              }
              startIcon={<Download className="w-4 h-4" />}
            >
              Download
            </Button>
            {pdfStatusCopy && (
              <Typography variant="small" className="text-gray-500 max-w-xs">
                {pdfStatusCopy}
              </Typography>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateDetail;
